import { useEffect } from "react";
import { Link } from "react-router-dom";
import { companyDetails } from "../data";

export default function Footer() {
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    if (window._iub && typeof window._iub.init === "function") {
      try {
        window._iub.init();
      } catch {
        // graceful fallback
      }
    }
  }, []);

  return (
    <footer className="bg-maersk-light text-maersk-dark pt-16 pb-8 border-t border-maersk-blue/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
          {/* Brand Column */}
          <div className="space-y-4">
            <Link to="/" className="flex items-center gap-3" aria-label="Lederskab.nu - Til forsiden">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=300,format=auto,quality=90/assets/22d442ab-45d9-4214-861d-1df67f744e88/lederskab-nu-logoer-2026-2.png"
                alt="Lederskab.nu - Det landsdækkende lederakademi"
                width="200"
                height="48"
                loading="lazy"
                decoding="async"
                className="h-12 w-auto object-contain"
              />
            </Link>
            <p className="text-navy-700 text-sm leading-relaxed">
              Professionel ledelsesudvikling, kurser og personlig rådgivning i hele Danmark. Vi hjælper ledere med at tage ansvar for eget lederskab.
            </p>
            <div className="flex gap-3 pt-2">
              <a
                href={companyDetails.socials.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-full bg-white border border-maersk-blue/10 flex items-center justify-center text-maersk-blue hover:bg-maersk-blue hover:text-white transition-all duration-300 shadow-xs"
                aria-label="Facebook"
              >
                <i className="fa-brands fa-facebook-f text-sm" />
              </a>
              <a
                href={companyDetails.socials.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-full bg-white border border-maersk-blue/10 flex items-center justify-center text-maersk-blue hover:bg-maersk-blue hover:text-white transition-all duration-300 shadow-xs"
                aria-label="LinkedIn"
              >
                <i className="fa-brands fa-linkedin-in text-sm" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-display text-base font-bold tracking-wide mb-5 text-maersk-dark border-l-2 border-maersk-blue pl-3">
              Hurtige Links
            </h3>
            <ul className="space-y-3 text-sm text-navy-700">
              <li>
                <Link to="/" className="hover:text-maersk-blue transition-colors duration-300">
                  Forside
                </Link>
              </li>
              <li>
                <Link to="/kurser" className="hover:text-maersk-blue transition-colors duration-300">
                  Lederforløb
                </Link>
              </li>
              <li>
                <Link to="/virksomheder" className="hover:text-maersk-blue transition-colors duration-300">
                  Virksomheder
                </Link>
              </li>
              <li>
                <Link to="/om-hende" className="hover:text-maersk-blue transition-colors duration-300">
                  Om Anita
                </Link>
              </li>
              <li>
                <Link to="/anbefalinger" className="hover:text-maersk-blue transition-colors duration-300">
                  Anbefalinger
                </Link>
              </li>
              <li>
                <Link to="/faq" className="hover:text-maersk-blue transition-colors duration-300">
                  FAQ
                </Link>
              </li>
              <li>
                <Link to="/kontakt" className="hover:text-maersk-blue transition-colors duration-300">
                  Kontakt
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Information */}
          <div>
            <h3 className="font-display text-base font-bold tracking-wide mb-5 text-maersk-dark border-l-2 border-maersk-blue pl-3">
              Kontaktoplysninger
            </h3>
            <ul className="space-y-3 text-sm text-navy-700">
              <li className="flex items-start gap-3">
                <i className="fa-solid fa-location-dot mt-1 text-maersk-blue" />
                <span>{companyDetails.address}</span>
              </li>
              <li className="flex items-center gap-3">
                <i className="fa-solid fa-phone text-maersk-blue" />
                <a
                  href={`tel:${companyDetails.phone.replace(/\s+/g, "")}`}
                  className="hover:text-maersk-blue transition-colors duration-300 whitespace-nowrap"
                >
                  {companyDetails.phone}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <i className="fa-solid fa-envelope text-maersk-blue" />
                <a
                  href={`mailto:${companyDetails.email}`}
                  className="hover:text-maersk-blue transition-colors duration-300 whitespace-nowrap"
                >
                  {companyDetails.email}
                </a>
              </li>
            </ul>
          </div>

          {/* Stressfri Kommunikation Partner */}
          <div className="bg-white p-6 rounded-xl border border-maersk-blue/10 space-y-4 shadow-xs">
            <h3 className="font-display text-base font-bold text-maersk-dark flex items-center gap-2">
              <i className="fa-solid fa-heart-pulse text-maersk-blue" />
              Stressfri Kommunikation
            </h3>
            <p className="text-xs text-navy-700 leading-relaxed">
              Lederskab arbejder tæt sammen med Stressfri Kommunikation for at skabe sunde og harmoniske arbejdsmiljøer i hele Danmark.
            </p>
            <a
              href={companyDetails.stressfriUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-xs font-bold text-maersk-blue hover:text-maersk-hover transition-colors duration-300 group"
            >
              Besøg Stressfri Kommunikation
              <i className="fa-solid fa-arrow-up-right-from-square text-[10px] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
            </a>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-maersk-blue/10 flex flex-col sm:flex-row justify-between items-center gap-4 text-xs text-neutral-500">
          <p>© {currentYear} {companyDetails.name}. Alle rettigheder forbeholdes.</p>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <a
              href="https://www.iubenda.com/privacy-policy/49561156"
              className="iubenda-white iubenda-noiframe iubenda-embed hover:underline"
              title="Privatlivspolitik "
            >
              Privatlivspolitik
            </a>
            <span className="text-neutral-300">•</span>
            <a
              href="https://www.iubenda.com/privacy-policy/49561156/cookie-policy"
              className="iubenda-white iubenda-noiframe iubenda-embed hover:underline"
              title="Cookiepolitik "
            >
              Cookiepolitik
            </a>
            <span className="text-neutral-300 hidden sm:inline">•</span>
            <span>Udviklet med fokus på personligt lederskab i hele Danmark</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
